import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

const team = [
  {
    name: 'James Harrington',
    role: 'CEO & Founder',
    bio: 'With over 15 years of experience in the furniture industry, James founded RETAILFLOW LLC with a vision to bring quality furniture to every American home at honest prices.',
    image: 'https://public.readdy.ai/ai/img_res/99ead34d556675031a38fded1089bf9f.jpg',
  },
  {
    name: 'Rachel Monroe',
    role: 'Head of Product Design',
    bio: 'Rachel leads our design team, ensuring every piece in our catalog meets the highest standards of craftsmanship, comfort, and contemporary style.',
    image: 'https://readdy.ai/api/search-image?query=professional%20portrait%20of%20female%20product%20designer%20in%20studio%20warm%20neutral%20tones%20modern%20minimalist%20background%20editorial%20photography%20confident%20smile&width=400&height=480&seq=sw-tm2&orientation=portrait',
  },
  {
    name: 'David Chen',
    role: 'Operations Director',
    bio: 'David oversees our supply chain and logistics from our Phoenix headquarters, ensuring every order is fulfilled accurately and delivered on time.',
    image: 'https://readdy.ai/api/search-image?query=professional%20portrait%20of%20asian%20male%20operations%20manager%20modern%20office%20setting%20warm%20light%20business%20casual%20attire%20editorial%20corporate%20photography&width=400&height=480&seq=sw-tm3&orientation=portrait',
  },
];

const milestones = [
  { year: '2010', event: 'RETAILFLOW LLC was founded in Phoenix, Arizona, with a mission to make premium furniture accessible to all.' },
  { year: '2013', event: 'Expanded our catalog to over 500 SKUs across sofas, tables, chairs, and bedroom furniture.' },
  { year: '2016', event: 'Opened our flagship showroom at 2825 W Berridge Ln, Phoenix, AZ — welcoming thousands of local customers.' },
  { year: '2019', event: 'Launched nationwide delivery service, bringing SWIFTMARKET quality to customers across the United States.' },
  { year: '2022', event: 'Achieved 10,000+ satisfied customers and introduced our Bespoke Custom Order program.' },
  { year: '2025', event: 'Celebrating 15 years of excellence with our Spring 2025 Collection — refined, durable, and designed for real life.' },
];

const values = [
  { icon: 'ri-star-line', title: 'Uncompromising Quality', desc: 'Every piece we carry is rigorously selected and inspected to meet our high standards of durability and finish.' },
  { icon: 'ri-customer-service-2-line', title: 'Customer First', desc: 'Your satisfaction is our priority. Our team is always available to guide you from selection through delivery.' },
  { icon: 'ri-leaf-line', title: 'Responsible Sourcing', desc: 'We partner with suppliers who share our commitment to sustainability and ethical manufacturing practices.' },
  { icon: 'ri-truck-line', title: 'Reliable Delivery', desc: 'With our nationwide logistics network, we ensure your furniture arrives safely and on schedule.' },
];

export default function AboutPage() {
  return (
    <div>
      <Navbar />

      {/* Hero */}
      <section className="w-full relative h-[480px] md:h-[580px] overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=modern%20furniture%20warehouse%20showroom%20interior%20professional%20lighting%20organized%20product%20displays%20sofas%20chairs%20tables%20clean%20bright%20american%20retail%20space%20editorial%20photography&width=1600&height=600&seq=sw-ab-hero&orientation=landscape"
          alt="SWIFTMARKET LLC Showroom"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/65 via-black/40 to-transparent" />
        <div className="absolute inset-0 flex flex-col justify-center px-8 md:px-20 max-w-2xl">
          <p className="text-white/60 text-xs tracking-[0.3em] uppercase mb-4">About RETAILFLOW LLC</p>
          <h1
            className="text-5xl md:text-6xl font-bold text-white leading-tight"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Quality You Can<br />
            <em className="not-italic text-[#d4b896]">Trust</em>
          </h1>
          <p className="text-white/70 mt-5 text-lg leading-relaxed">
            Phoenix-based furniture company dedicated to delivering premium pieces with honest pricing and exceptional service.
          </p>
        </div>
      </section>

      {/* Company Profile */}
      <section className="w-full bg-[#faf8f5] py-24 px-6 md:px-16">
        <div className="max-w-7xl mx-auto flex flex-col lg:flex-row gap-16 items-center">
          <div className="flex-1">
            <p className="text-xs uppercase tracking-[0.3em] text-[#8B6348] mb-4 font-medium">Company Profile</p>
            <h2
              className="text-4xl md:text-5xl font-bold text-[#2c2420] mb-8 leading-tight"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Who We Are
            </h2>
            <p className="text-stone-500 text-base leading-relaxed mb-5">
              <strong>RETAILFLOW LLC</strong> is a Phoenix, Arizona-based furniture company founded with a simple belief: everyone deserves beautiful, lasting furniture without compromise. Since 2010, we have built our reputation on quality products, transparent pricing, and a customer-first approach.
            </p>
            <p className="text-stone-500 text-base leading-relaxed mb-5">
              Headquartered at <strong>4131 W Morten Ave, Phoenix, AZ 85051</strong>, our showroom spans thousands of square feet of curated furniture for living rooms, dining spaces, bedrooms, and home offices. We serve both residential and commercial clients across the United States.
            </p>
            <p className="text-stone-500 text-base leading-relaxed mb-8">
              As a registered LLC (EIN: <strong>41-3974436</strong>), we operate with full transparency and accountability, ensuring every transaction and interaction reflects our values of integrity and excellence.
            </p>
            <div className="flex flex-wrap gap-10">
              {[['15+', 'Years in Business'], ['10,000+', 'Happy Customers'], ['500+', 'Products Available'], ['48', 'States Delivered']].map(([num, label]) => (
                <div key={label}>
                  <p className="text-3xl font-bold text-[#8B6348]" style={{ fontFamily: "'Playfair Display', serif" }}>{num}</p>
                  <p className="text-stone-400 text-xs mt-1">{label}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="w-full lg:w-[460px] h-[440px] rounded-3xl overflow-hidden flex-shrink-0">
            <img
              src="https://readdy.ai/api/search-image?query=luxury%20furniture%20showroom%20interior%20warm%20professional%20lighting%20sofa%20display%20sectional%20couch%20modern%20american%20store%20natural%20wood%20tones%20editorial%20photography&width=600&height=550&seq=sw-ab-phil&orientation=portrait"
              alt="SWIFTMARKET LLC Showroom Interior"
              className="w-full h-full object-cover object-top"
            />
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="w-full bg-white py-24 px-6 md:px-16">
        <div className="max-w-7xl mx-auto">
          <p className="text-xs uppercase tracking-[0.3em] text-[#8B6348] mb-3 font-medium text-center">What Drives Us</p>
          <h2
            className="text-4xl font-bold text-[#2c2420] text-center mb-14"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Our Core Values
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((v) => (
              <div key={v.title} className="bg-[#faf8f5] rounded-2xl p-7">
                <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-[#f0e6d8] mb-5">
                  <i className={`${v.icon} text-[#8B6348] text-xl`} />
                </div>
                <h3 className="text-[#2c2420] font-semibold text-base mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
                  {v.title}
                </h3>
                <p className="text-stone-400 text-sm leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="w-full bg-[#faf8f5] py-24 px-6 md:px-16">
        <div className="max-w-4xl mx-auto">
          <p className="text-xs uppercase tracking-[0.3em] text-[#8B6348] mb-3 font-medium text-center">Our Journey</p>
          <h2
            className="text-4xl font-bold text-[#2c2420] text-center mb-14"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Company Milestones
          </h2>
          <div className="relative">
            <div className="absolute left-[72px] top-0 bottom-0 w-px bg-stone-200 hidden md:block" />
            <div className="space-y-8">
              {milestones.map((m) => (
                <div key={m.year} className="flex gap-6 md:gap-10 items-start">
                  <div className="flex-shrink-0 w-14 md:w-16 text-right">
                    <span className="text-[#8B6348] text-sm font-bold">{m.year}</span>
                  </div>
                  <div className="hidden md:flex items-start pt-1">
                    <div className="w-3 h-3 rounded-full bg-[#8B6348] ring-4 ring-[#f0e6d8] flex-shrink-0 relative z-10" />
                  </div>
                  <div className="flex-1 bg-white rounded-xl p-5">
                    <p className="text-stone-600 text-sm leading-relaxed">{m.event}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="w-full bg-white py-24 px-6 md:px-16">
        <p className="text-xs uppercase tracking-[0.3em] text-[#8B6348] mb-3 font-medium text-center">The People Behind It</p>
        <h2
          className="text-4xl font-bold text-[#2c2420] text-center mb-14"
          style={{ fontFamily: "'Playfair Display', serif" }}
        >
          Meet Our Leadership
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 max-w-5xl mx-auto">
          {team.map((member) => (
            <article key={member.name} className="text-center group">
              <div className="w-full h-[300px] rounded-2xl overflow-hidden mb-5">
                <img
                  src={member.image}
                  alt={member.name}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <h3
                className="text-[#2c2420] font-semibold text-lg"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                {member.name}
              </h3>
              <p className="text-[#8B6348] text-xs uppercase tracking-wider mt-1 mb-3">{member.role}</p>
              <p className="text-stone-400 text-sm leading-relaxed">{member.bio}</p>
            </article>
          ))}
        </div>
      </section>

      <Footer />
    </div>
  );
}
